import { useEffect, useMemo, useState } from "react";
import { CheckCircle2, CircleAlert, Download, Monitor, RefreshCw, Share2, Smartphone, Wifi } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import type { Language } from "@/lib/finance";

type InstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
};

let capturedInstallPrompt: InstallPromptEvent | null = null;
let installObserved = false;
const installListeners = new Set<() => void>();

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (event) => {
    event.preventDefault();
    capturedInstallPrompt = event as InstallPromptEvent;
    installListeners.forEach((listener) => listener());
  });
  window.addEventListener("appinstalled", () => {
    installObserved = true;
    capturedInstallPrompt = null;
    installListeners.forEach((listener) => listener());
  });
}

type InstallAppGuideProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  language: Language;
};

function isStandalone() {
  if (typeof window === "undefined") return false;
  return window.matchMedia("(display-mode: standalone)").matches
    || Boolean((window.navigator as Navigator & { standalone?: boolean }).standalone);
}

export function InstallAppGuide({ open, onOpenChange, language }: InstallAppGuideProps) {
  const [installPrompt, setInstallPrompt] = useState<InstallPromptEvent | null>(capturedInstallPrompt);
  const [installed, setInstalled] = useState(() => installObserved || isStandalone());
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const syncInstallState = () => {
      setInstallPrompt(capturedInstallPrompt);
      setInstalled(installObserved || isStandalone());
    };
    installListeners.add(syncInstallState);
    syncInstallState();
    return () => {
      installListeners.delete(syncInstallState);
    };
  }, []);

  const platform = useMemo(() => {
    const agent = navigator.userAgent.toLowerCase();
    if (/iphone|ipad|ipod/.test(agent)) return "ios";
    if (/android/.test(agent)) return "android";
    return "desktop";
  }, []);

  const text = language === "id" ? {
    title: "Unduh aplikasi",
    description: "Pasang EventKita Finance di layar utama agar dapat dibuka seperti aplikasi.",
    installed: "Sudah terpasang",
    ready: "Siap dipasang",
    manual: "Pemasangan manual",
    updateTitle: "Perbarui aplikasi terlebih dahulu",
    updateBody: "Pastikan aplikasi Google dan Google Chrome sudah menggunakan versi terbaru. Perbarui keduanya melalui Play Store bila tombol instalasi belum muncul.",
    androidTitle: "Android · Google Chrome",
    androidSteps: ["Buka dashboard ini menggunakan Google Chrome.", "Tekan Pasang sekarang di bawah. Jika tombol belum tersedia, buka menu ⋮ Chrome.", "Pilih Instal aplikasi atau Tambahkan ke layar utama, lalu konfirmasi."],
    iosTitle: "iPhone/iPad · Safari",
    iosSteps: ["Buka dashboard ini menggunakan Safari.", "Tekan ikon Bagikan di bagian bawah layar.", "Pilih Tambahkan ke Layar Utama, lalu tekan Tambah."],
    desktopTitle: "Komputer · Chrome atau Edge",
    desktopSteps: ["Buka dashboard menggunakan Chrome atau Microsoft Edge.", "Tekan Pasang sekarang, atau pilih ikon instalasi di sisi kanan kolom alamat.", "Konfirmasi pemasangan; aplikasi akan tersedia dari Start Menu atau desktop."],
    connectionTitle: "Data tetap aman dan terbaru",
    connectionBody: "PWA hanya menyimpan aset tampilan. Login, transaksi, file impor, dan respons Supabase tidak disimpan oleh cache PWA serta tetap membutuhkan koneksi internet.",
    troubleshootTitle: "Tombol instalasi belum muncul?",
    troubleshootBody: "Perbarui browser, buka dashboard langsung melalui alamat Pages—bukan browser di dalam WhatsApp—kemudian muat ulang. Pastikan Anda tidak sedang memakai mode samaran.",
    install: "Pasang sekarang",
    installing: "Membuka instalasi…",
    close: "Tutup",
  } : {
    title: "Download app",
    description: "Install EventKita Finance on your home screen and open it like an app.",
    installed: "Already installed",
    ready: "Ready to install",
    manual: "Manual installation",
    updateTitle: "Update your apps first",
    updateBody: "Make sure the Google app and Google Chrome are on their latest versions. Update both from the Play Store if the install option does not appear.",
    androidTitle: "Android · Google Chrome",
    androidSteps: ["Open this dashboard in Google Chrome.", "Select Install now below. If it is unavailable, open Chrome's ⋮ menu.", "Choose Install app or Add to Home screen, then confirm."],
    iosTitle: "iPhone/iPad · Safari",
    iosSteps: ["Open this dashboard in Safari.", "Select the Share icon at the bottom of the screen.", "Choose Add to Home Screen, then select Add."],
    desktopTitle: "Computer · Chrome or Edge",
    desktopSteps: ["Open the dashboard in Chrome or Microsoft Edge.", "Select Install now, or use the install icon on the right side of the address bar.", "Confirm; the app will be available from the Start Menu or desktop."],
    connectionTitle: "Your data stays safe and current",
    connectionBody: "The PWA only caches interface assets. Login, transactions, imported files, and Supabase responses are not stored by the PWA cache and still require an internet connection.",
    troubleshootTitle: "Install option not showing?",
    troubleshootBody: "Update the browser, open the dashboard directly from its Pages address—not an in-app WhatsApp browser—then reload. Make sure private browsing is off.",
    install: "Install now",
    installing: "Opening installer…",
    close: "Close",
  };

  const currentGuide = platform === "ios"
    ? { icon: <Share2 />, title: text.iosTitle, steps: text.iosSteps }
    : platform === "android"
      ? { icon: <Smartphone />, title: text.androidTitle, steps: text.androidSteps }
      : { icon: <Monitor />, title: text.desktopTitle, steps: text.desktopSteps };

  async function requestInstall() {
    if (!installPrompt) return;
    setBusy(true);
    try {
      await installPrompt.prompt();
      const choice = await installPrompt.userChoice;
      if (choice.outcome === "accepted") {
        installObserved = true;
        setInstalled(true);
      }
      capturedInstallPrompt = null;
      setInstallPrompt(capturedInstallPrompt);
    } finally {
      setBusy(false);
    }
  }

  return <Sheet open={open} onOpenChange={onOpenChange}>
    <SheetContent className="w-full overflow-y-auto sm:max-w-lg">
      <SheetHeader className="border-b px-4 py-5 sm:px-6">
        <img src="/eventkita-logo.webp" alt="EventKita" className="mb-2 h-20 w-16 rounded-lg bg-white p-1.5 object-contain" />
        <SheetTitle>{text.title}</SheetTitle>
        <SheetDescription>{text.description}</SheetDescription>
      </SheetHeader>

      <div className="space-y-4 px-4 pb-4 sm:px-6">
        <div className={`flex items-center justify-between gap-3 rounded-2xl border p-4 ${installed ? "border-emerald-500/30 bg-emerald-500/8" : installPrompt ? "border-primary/25 bg-primary/5" : "bg-muted/25"}`}>
          <div className="flex items-center gap-3">
            <span className="flex size-10 items-center justify-center rounded-xl bg-background text-primary shadow-sm">{installed ? <CheckCircle2 /> : <Smartphone />}</span>
            <div><p className="text-sm font-semibold">EventKita Finance</p><p className="text-xs text-muted-foreground">PWA · v0.19.5</p></div>
          </div>
          <Badge variant="outline">{installed ? text.installed : installPrompt ? text.ready : text.manual}</Badge>
        </div>

        {platform === "android" && <GuideNote icon={<RefreshCw />} title={text.updateTitle} body={text.updateBody} />}

        <section className="rounded-2xl border bg-card p-4">
          <div className="flex items-center gap-2 text-sm font-semibold"><span className="text-primary [&_svg]:size-5">{currentGuide.icon}</span>{currentGuide.title}</div>
          <ol className="mt-4 space-y-3">
            {currentGuide.steps.map((step, index) => <li key={step} className="flex gap-3 text-sm leading-6 text-muted-foreground"><span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">{index + 1}</span><span>{step}</span></li>)}
          </ol>
        </section>

        <GuideNote icon={<Wifi />} title={text.connectionTitle} body={text.connectionBody} />
        {!installed && !installPrompt && <GuideNote icon={<CircleAlert />} title={text.troubleshootTitle} body={text.troubleshootBody} />}
      </div>

      <SheetFooter className="sticky bottom-0 border-t bg-background/95 px-4 py-4 backdrop-blur sm:px-6">
        {!installed && installPrompt && <Button className="min-h-11 w-full" disabled={busy} onClick={() => void requestInstall()}><Download />{busy ? text.installing : text.install}</Button>}
        <Button variant={installed || !installPrompt ? "default" : "outline"} className="min-h-11 w-full" onClick={() => onOpenChange(false)}>{text.close}</Button>
      </SheetFooter>
    </SheetContent>
  </Sheet>;
}

function GuideNote({ icon, title, body }: { icon: React.ReactNode; title: string; body: string }) {
  return <section className="rounded-2xl border bg-muted/25 p-4">
    <div className="flex gap-3"><span className="mt-0.5 text-primary [&_svg]:size-5">{icon}</span><div><p className="text-sm font-semibold">{title}</p><p className="mt-1 text-sm leading-6 text-muted-foreground">{body}</p></div></div>
  </section>;
}
